import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ConsoleShell, PageHead } from "../components/ConsoleShell.js";
import { Card } from "../components/Card.js";
import { Tag, Btn, Tabs, TabPanel, Toggle } from "../components/index.js";
import { Banner } from "../components/Banner.js";
import { Icon } from "../components/Icon.js";
import { LoadingState, ErrorState, EmptyState } from "../components/EmptyState.js";
import { useAsync } from "../lib/useAsync.js";
import {
  manuals,
  type TranslationSummary,
  type TranslationState,
  type TranslatedStep,
} from "../lib/api.js";
import { TARGET_LOCALES, localeLabel } from "../lib/demo.js";
import type { Manual, Step } from "@tebiki/shared";

/** Locale columns shown in the status matrix (alongside the JA master). */
const LOCALES = [...TARGET_LOCALES];

/** Render the source ("原文") cell — always present, never re-translated. */
const sourceTag = () => (
  <span
    className="tag"
    style={{ color: "var(--ok)", borderColor: "transparent", background: "color-mix(in srgb,var(--ok) 18%,var(--surface))" }}
  >
    原文
  </span>
);

/** Map a per-locale translation state onto the prototype's tag vocabulary. */
function stateCell(t: TranslationSummary | undefined, onTranslate: () => void, busy: boolean) {
  const state: TranslationState = t?.state ?? "none";
  if (state === "ready") {
    return (
      <span style={{ display: "inline-flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
        <span
          className="tag"
          style={{ color: "var(--ok)", borderColor: "transparent", background: "color-mix(in srgb,var(--ok) 18%,var(--surface))" }}
        >
          公開
        </span>
        {t?.isHumanReviewed ? (
          <Tag dot color="var(--info)">
            人手レビュー済み
          </Tag>
        ) : null}
      </span>
    );
  }
  if (state === "draft") return <Tag hi>要レビュー</Tag>;
  if (state === "translating" || state === "queued") {
    return (
      <Tag dot color="var(--hi)">
        {state === "queued" ? "翻訳待ち" : "翻訳中"}
      </Tag>
    );
  }
  // none → offer the translate action
  return (
    <button
      className="btn"
      style={{ minHeight: 44, padding: "5px 12px", fontSize: 12 }}
      onClick={onTranslate}
      disabled={busy}
      aria-label="この言語に翻訳"
    >
      {busy ? (
        "依頼中…"
      ) : (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Icon name="plus" size={14} />
          翻訳
        </span>
      )}
    </button>
  );
}

export function Multilingual() {
  const [tab, setTab] = useState("status");
  const [params] = useSearchParams();
  const focusId = params.get("id");

  // Manuals to show in the matrix (focus a single manual when arriving from Preview).
  const manualState = useAsync<Manual[]>(async () => {
    const { items } = await manuals.list();
    return items;
  }, []);

  const visibleManuals = useMemo(() => {
    const all = manualState.data ?? [];
    if (focusId) {
      const one = all.filter((m) => m.manualId === focusId);
      if (one.length) return one;
    }
    return all;
  }, [manualState.data, focusId]);

  return (
    <ConsoleShell
      breadcrumb={["TEBIKI", "多言語"]}
      actions={
        <Btn
          variant="primary"
          icon={<Icon name="multilingual" />}
          onClick={() => visibleManuals.forEach((m) => void translateAll(m.manualId))}
          disabled={!visibleManuals.length}
        >
          一括で翻訳
        </Btn>
      }
    >
      <PageHead title={<>多言語<span className="hl">展開</span></>}>
        日本語で撮るだけ。原文は保持したまま、各言語へAI翻訳＋多言語TTSで自動配布。
      </PageHead>

      <Tabs
        active={tab}
        onChange={setTab}
        ariaLabel="多言語ビュー"
        tabs={[
          { id: "status", label: "言語別の状況" },
          { id: "editor", label: "翻訳エディタ" },
          { id: "glossary", label: "用語集（用語ロック）" },
        ]}
      />

      <TabPanel id="status" active={tab}>
        <Banner variant="info" className="reveal">
          <span>
            <b>原文保持:</b> マスター言語（日本語）は不変。各言語は派生として再生成可能。未翻訳は原文にフォールバック。
          </span>
        </Banner>
        {manualState.loading ? (
          <LoadingState label="マニュアルを読み込み中…" />
        ) : manualState.error ? (
          <ErrorState message={manualState.error} offline={manualState.offline} onRetry={manualState.reload} />
        ) : !visibleManuals.length ? (
          <EmptyState title="マニュアルがありません">
            マニュアルを作成すると、ここで各言語への展開状況を管理できます。
          </EmptyState>
        ) : (
          <div className="list reveal d1" style={{ marginTop: 18, overflowX: "auto" }}>
            <div className="lrow lhead lcols" style={lcols} role="row">
              <span>マニュアル</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Icon name="flag" size={14} /> JA 原文
              </span>
              {LOCALES.map((loc) => (
                <span key={loc} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Icon name="flag" size={14} /> {loc.toUpperCase()}
                </span>
              ))}
            </div>
            {visibleManuals.map((m) => (
              <ManualRow key={m.manualId} manual={m} />
            ))}
          </div>
        )}
      </TabPanel>

      <TabPanel id="editor" active={tab}>
        {manualState.loading ? (
          <LoadingState label="マニュアルを読み込み中…" />
        ) : manualState.error ? (
          <ErrorState message={manualState.error} offline={manualState.offline} onRetry={manualState.reload} />
        ) : !visibleManuals.length ? (
          <EmptyState title="マニュアルがありません">
            マニュアルを作成すると、ここで対訳を編集できます。
          </EmptyState>
        ) : (
          <TranslationEditor manualsList={visibleManuals} initialId={focusId} />
        )}
      </TabPanel>

      <TabPanel id="glossary" active={tab}>
        <Card crop pad className="reveal d2">
          <div className="spread">
            <div className="kicker">用語ロック（翻訳時に対訳を強制）</div>
            <Btn
              style={{ minHeight: 38, padding: "8px 14px" }}
              icon={<Icon name="plus" size={14} />}
              disabled
              aria-disabled="true"
              title="準備中"
            >
              用語追加
            </Btn>
          </div>
          <div className="grid g-3" style={{ marginTop: 14 }}>
            <div className="chip" style={{ justifyContent: "space-between" }}>
              ドロア <span className="mono" style={{ color: "var(--muted)" }}>→ Drawer</span>
            </div>
            <div className="chip" style={{ justifyContent: "space-between" }}>
              ミシン目 <span className="mono" style={{ color: "var(--muted)" }}>→ perforation</span>
            </div>
            <div className="chip" style={{ justifyContent: "space-between" }}>
              SANGARIA <span className="tag" style={{ fontSize: 9 }}>非翻訳</span>
            </div>
          </div>
        </Card>
      </TabPanel>
    </ConsoleShell>
  );

  /** Best-effort bulk translate into every offered locale (used by the topbar action).
   *  One request per locale — a single request looping 12 Gemini calls would outlive the
   *  Worker request window; per-locale requests also surface progress sooner. */
  async function translateAll(manualId: string) {
    for (const locale of LOCALES) {
      try {
        await manuals.translate(manualId, [locale]);
      } catch {
        /* best-effort: the row's own status fetch governs the displayed state */
      }
    }
  }
}

/** One manual row: loads its translation status, renders per-locale cells + translate actions. */
function ManualRow({ manual }: { manual: Manual }) {
  const { data, reload } = useAsync<TranslationSummary[]>(async () => {
    const res = await manuals.translations(manual.manualId);
    return res.translations;
  }, [manual.manualId]);
  const [busy, setBusy] = useState<string | null>(null);

  const byLocale = useMemo(() => {
    const map = new Map<string, TranslationSummary>();
    (data ?? []).forEach((t) => map.set(t.locale, t));
    return map;
  }, [data]);

  async function translate(locale: string) {
    setBusy(locale);
    try {
      await manuals.translate(manual.manualId, [locale]);
      reload();
    } catch {
      /* in demo/offline mode the optimistic reload keeps the demo state */
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="lrow lcols" style={lcols} role="row">
      <div className="display jp" style={{ fontSize: 16 }}>
        {manual.title}
      </div>
      {sourceTag()}
      {LOCALES.map((loc) => (
        <span key={loc}>{stateCell(byLocale.get(loc), () => void translate(loc), busy === loc)}</span>
      ))}
    </div>
  );
}

/** Matrix columns scale with the offered locales; rows carry a minWidth so the whole
 *  matrix horizontally scrolls inside .list on narrow viewports instead of crushing. */
const lcols = {
  gridTemplateColumns: `minmax(200px, 1fr) 96px repeat(${LOCALES.length}, 118px)`,
  minWidth: 200 + 96 + LOCALES.length * 118 + 32,
} as const;

// ---------- translation editor ----------

/** Editable target-locale field set, keyed by source step id. */
type EditMap = Record<string, { heading: string; caption: string }>;

/** Build the initial edit map from translated steps (falls back to source order). */
function toEditMap(steps: Step[]): EditMap {
  const map: EditMap = {};
  steps.forEach((s) => {
    map[s.stepId] = { heading: s.title ?? "", caption: s.caption ?? "" };
  });
  return map;
}

/**
 * Side-by-side 対訳 editor: pick a manual + locale, load the translated steps, and edit them
 * against the immutable JA source. Untranslated locales prompt to translate first; edits are
 * persisted via manuals.saveTranslation. Tolerant of an unreachable API (demo fallback).
 */
function TranslationEditor({ manualsList, initialId }: { manualsList: Manual[]; initialId: string | null }) {
  const firstId = manualsList[0]?.manualId ?? "";
  const [manualId, setManualId] = useState(
    initialId && manualsList.some((m) => m.manualId === initialId) ? initialId : firstId,
  );
  const [locale, setLocale] = useState<string>(TARGET_LOCALES[0]);

  // Source (原文) — the immutable JA master.
  const source = useAsync<Manual>(() => manuals.get(manualId), [manualId]);

  // Per-manual translation status (drives the "まず翻訳" prompt per locale).
  const status = useAsync<TranslationSummary[]>(
    async () => (await manuals.translations(manualId)).translations,
    [manualId],
  );

  // Target (訳文) for the chosen locale.
  const target = useAsync<{ state: TranslationState; steps: Step[] }>(
    async () => {
      const t = await manuals.translation(manualId, locale);
      return { state: t.state, steps: t.steps };
    },
    [manualId, locale],
  );

  const localeStatus = useMemo(
    () => (status.data ?? []).find((t) => t.locale === locale),
    [status.data, locale],
  );
  const localeState: TranslationState = target.data?.state ?? localeStatus?.state ?? "none";
  const untranslated = localeState === "none";

  const [edits, setEdits] = useState<EditMap>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [reviewed, setReviewed] = useState(false);

  // Re-seed the editable fields whenever the loaded translation changes.
  useEffect(() => {
    if (target.data && !untranslated) {
      setEdits(toEditMap(target.data.steps));
      setDirty(false);
      setSaved(false);
    }
  }, [target.data, untranslated]);

  useEffect(() => {
    setReviewed(localeStatus?.isHumanReviewed ?? localeState === "ready");
  }, [localeStatus?.isHumanReviewed, localeState]);

  function setField(stepId: string, field: "heading" | "caption", value: string) {
    setEdits((prev) => ({ ...prev, [stepId]: { ...prev[stepId], [field]: value } }));
    setDirty(true);
    setSaved(false);
  }

  function buildSteps(): TranslatedStep[] {
    return Object.entries(edits).map(([stepId, v]) => ({
      stepId,
      heading: v.heading,
      caption: v.caption,
    }));
  }

  async function save(opts?: { isHumanReviewed?: boolean; state?: TranslationState }) {
    setSaving(true);
    try {
      await manuals.saveTranslation(manualId, locale, {
        steps: buildSteps(),
        ...(opts?.state ? { state: opts.state } : {}),
        ...(opts?.isHumanReviewed != null ? { isHumanReviewed: opts.isHumanReviewed } : {}),
      });
    } catch {
      /* best-effort: in demo/offline mode we still reflect the optimistic UI state */
    } finally {
      setSaving(false);
      setDirty(false);
      setSaved(true);
    }
  }

  async function onToggleReviewed(next: boolean) {
    setReviewed(next);
    await save(next ? { isHumanReviewed: true, state: "ready" } : { isHumanReviewed: false });
  }

  async function translateNow() {
    try {
      await manuals.translate(manualId, [locale]);
    } catch {
      /* best-effort */
    } finally {
      status.reload();
      target.reload();
    }
  }

  const sourceSteps = source.data?.steps ?? [];
  const localeName = localeLabel(locale).name;

  return (
    <>
      <Card crop pad className="reveal">
        <div className="kicker">翻訳エディタ — 原文と対訳を並べて確認</div>
        <div className="grid g-2" style={{ marginTop: 14, alignItems: "end" }}>
          <div>
            <label className="field-label" htmlFor="te-manual">
              マニュアル
            </label>
            <select
              id="te-manual"
              className="select"
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
            >
              {manualsList.map((m) => (
                <option key={m.manualId} value={m.manualId}>
                  {m.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="field-label" htmlFor="te-locale">
              対象言語
            </label>
            <select
              id="te-locale"
              className="select"
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
            >
              {TARGET_LOCALES.map((loc) => (
                <option key={loc} value={loc}>
                  {localeLabel(loc).name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {source.loading || target.loading ? (
        <LoadingState label="対訳を読み込み中…" />
      ) : source.error ? (
        <ErrorState message={source.error} offline={source.offline} onRetry={source.reload} />
      ) : untranslated ? (
        <EmptyState
          icon={<Icon name="multilingual" size={30} />}
          title={`${localeName} はまだ翻訳されていません`}
          action={
            <Btn variant="primary" icon={<Icon name="multilingual" />} onClick={() => void translateNow()}>
              まず翻訳する
            </Btn>
          }
        >
          この言語の対訳を作成するには、先にAI翻訳を実行してください。完了後にここで編集できます。
        </EmptyState>
      ) : !sourceSteps.length ? (
        <EmptyState title="手順がありません">このマニュアルにはまだ手順がありません。</EmptyState>
      ) : (
        <>
          <div
            className="reveal d1"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
              gap: 14,
              alignItems: "stretch",
              marginTop: 16,
            }}
            role="list"
            aria-label={`原文と ${localeName} の対訳`}
          >
            {sourceSteps.map((s) => {
              const e = edits[s.stepId] ?? { heading: "", caption: "" };
              return (
                <ParallelRow
                  key={s.stepId}
                  step={s}
                  heading={e.heading}
                  caption={e.caption}
                  localeName={localeName}
                  onChange={(field, value) => setField(s.stepId, field, value)}
                />
              );
            })}
          </div>

          <Card pad className="reveal d2" style={{ marginTop: 16 }}>
            <div className="spread" style={{ flexWrap: "wrap", gap: 14 }}>
              <label
                className="row"
                style={{ gap: 10, cursor: "pointer" }}
                onClick={(ev) => ev.preventDefault()}
              >
                <Toggle
                  checked={reviewed}
                  onChange={(next) => void onToggleReviewed(next)}
                  label="人手レビュー済みにする"
                />
                <span className="jp" aria-hidden="true">
                  人手レビュー済みにする
                  <span style={{ color: "var(--muted)", fontSize: 13, marginLeft: 8 }}>
                    （state: reviewed として公開可能に）
                  </span>
                </span>
              </label>
              <div className="row" style={{ gap: 10 }}>
                {saved && !dirty ? (
                  <Tag dot color="var(--ok)">
                    保存しました
                  </Tag>
                ) : dirty ? (
                  <Tag dot color="var(--hi)">
                    未保存の変更
                  </Tag>
                ) : null}
                <Btn variant="primary" onClick={() => void save()} disabled={saving || !dirty}>
                  {saving ? "保存中…" : "保存"}
                </Btn>
              </div>
            </div>
          </Card>
        </>
      )}
    </>
  );
}

/** One source/target step pair rendered as two adjacent cells. */
function ParallelRow({
  step,
  heading,
  caption,
  localeName,
  onChange,
}: {
  step: Step;
  heading: string;
  caption: string;
  localeName: string;
  onChange: (field: "heading" | "caption", value: string) => void;
}) {
  const headId = `tr-head-${step.stepId}`;
  const capId = `tr-cap-${step.stepId}`;
  return (
    <>
      {/* 原文 (read-only source) */}
      <Card crop pad role="listitem" style={{ background: "var(--bg2)" }}>
        <div className="spread" style={{ alignItems: "baseline" }}>
          <span className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>
            手順 {step.order}
          </span>
          <span
            className="tag"
            style={{ color: "var(--ok)", borderColor: "transparent", background: "color-mix(in srgb,var(--ok) 18%,var(--surface))", display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <Icon name="flag" size={12} /> 原文
          </span>
        </div>
        <div className="display jp" style={{ fontSize: 17, margin: "6px 0 8px" }}>
          {step.title}
        </div>
        <p className="jp" style={{ margin: 0, color: "var(--muted)", fontSize: 14, lineHeight: 1.6 }}>
          {step.caption}
        </p>
      </Card>

      {/* 訳文 (editable target) */}
      <Card pad role="listitem">
        <div className="spread" style={{ alignItems: "baseline" }}>
          <span className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>
            STEP {step.order}
          </span>
          <span className="tag" style={{ color: "var(--hi)", borderColor: "transparent", background: "color-mix(in srgb,var(--hi) 18%,var(--surface))" }}>
            {localeName}（訳文）
          </span>
        </div>
        <label className="field-label" htmlFor={headId} style={{ marginTop: 6 }}>
          見出し
        </label>
        <input
          id={headId}
          className="input"
          value={heading}
          placeholder={`${localeName} の見出し`}
          onChange={(e) => onChange("heading", e.target.value)}
        />
        <label className="field-label" htmlFor={capId} style={{ marginTop: 10 }}>
          説明
        </label>
        <textarea
          id={capId}
          className="area"
          rows={3}
          value={caption}
          placeholder={`${localeName} の説明`}
          onChange={(e) => onChange("caption", e.target.value)}
          style={{ resize: "vertical", lineHeight: 1.6 }}
        />
      </Card>
    </>
  );
}
